// src/components/tutorials/JS_Number_Properties.jsx
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Number_Properties() {
  const refs = {
    MAX_VALUE: useRef(null),
    MIN_VALUE: useRef(null),
    NaN: useRef(null),
    NEGATIVE_INFINITY: useRef(null),
    POSITIVE_INFINITY: useRef(null),
    EPSILON: useRef(null),
    MIN_SAFE_INTEGER: useRef(null),
    MAX_SAFE_INTEGER: useRef(null),
  };

  const scrollTo = (key) =>
    refs[key]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔢 JavaScript Number Properties</h1>
      <p className="tutorial-intro">
        JavaScript provides several <b>read-only number properties</b> that
        belong to the <code>Number</code> object.
      </p>

      {/* Two-column Key Section */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "2rem",
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1.5rem",
          borderRadius: "12px",
          marginTop: "1rem",
        }}
      >
        {/* Column 1 */}
        <div>
          <h2 style={{ margin: 0, color: "#92400e" }}>Number Properties</h2>
          <ul
            style={{ listStyleType: "none", padding: 0, marginTop: "0.5rem" }}
          >
            {[
              { key: "MAX_VALUE", label: "Number.MAX_VALUE" },
              { key: "MIN_VALUE", label: "Number.MIN_VALUE" },
              { key: "NaN", label: "Number.NaN" },
              { key: "NEGATIVE_INFINITY", label: "Number.NEGATIVE_INFINITY" },
            ].map((item) => (
              <li key={item.key}>
                <button
                  onClick={() => scrollTo(item.key)}
                  style={{
                    background: "transparent",
                    border: "none",
                    textDecoration: "underline",
                    color: "black",
                    cursor: "pointer",
                    fontSize: "1.05rem",
                    marginBottom: "0.3rem",
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Column 2 */}
        <div>
          <h2 style={{ margin: 0, color: "#92400e" }}>&nbsp;</h2>
          <ul
            style={{ listStyleType: "none", padding: 0, marginTop: "0.5rem" }}
          >
            {[
              { key: "POSITIVE_INFINITY", label: "Number.POSITIVE_INFINITY" },
              { key: "EPSILON", label: "Number.EPSILON" },
              { key: "MIN_SAFE_INTEGER", label: "Number.MIN_SAFE_INTEGER" },
              { key: "MAX_SAFE_INTEGER", label: "Number.MAX_SAFE_INTEGER" },
            ].map((item) => (
              <li key={item.key}>
                <button
                  onClick={() => scrollTo(item.key)}
                  style={{
                    background: "transparent",
                    border: "none",
                    textDecoration: "underline",
                    color: "black",
                    cursor: "pointer",
                    fontSize: "1.05rem",
                    marginBottom: "0.3rem",
                  }}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Detailed Sections */}

      {/* MAX_VALUE */}
      <div ref={refs.MAX_VALUE} className="tutorial-section">
        <h3>1️⃣ Number.MAX_VALUE</h3>
        <p>The largest positive numeric value representable in JavaScript.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(Number.MAX_VALUE); // 1.7976931348623157e+308`}
          />
          <TryItButton code={`console.log(Number.MAX_VALUE);`} />
        </div>
        <OutputBox>Output: 1.7976931348623157e+308</OutputBox>
      </div>

      {/* MIN_VALUE */}
      <div ref={refs.MIN_VALUE} className="tutorial-section">
        <h3>2️⃣ Number.MIN_VALUE</h3>
        <p>
          The smallest positive numeric value representable (closest to zero).
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock code={`console.log(Number.MIN_VALUE); // 5e-324`} />
          <TryItButton code={`console.log(Number.MIN_VALUE);`} />
        </div>
        <OutputBox>Output: 5e-324</OutputBox>
      </div>

      {/* NaN */}
      <div ref={refs.NaN} className="tutorial-section">
        <h3>3️⃣ Number.NaN</h3>
        <p>Represents “Not-a-Number” value.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock code={`console.log(Number.NaN); // NaN`} />
          <TryItButton code={`console.log(Number.NaN);`} />
        </div>
        <OutputBox>Output: NaN</OutputBox>
      </div>

      {/* NEGATIVE_INFINITY */}
      <div ref={refs.NEGATIVE_INFINITY} className="tutorial-section">
        <h3>4️⃣ Number.NEGATIVE_INFINITY</h3>
        <p>Represents negative infinity (-Infinity).</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(Number.NEGATIVE_INFINITY); // -Infinity`}
          />
          <TryItButton code={`console.log(Number.NEGATIVE_INFINITY);`} />
        </div>
        <OutputBox>Output: -Infinity</OutputBox>
      </div>

      {/* POSITIVE_INFINITY */}
      <div ref={refs.POSITIVE_INFINITY} className="tutorial-section">
        <h3>5️⃣ Number.POSITIVE_INFINITY</h3>
        <p>Represents positive infinity (+Infinity).</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(Number.POSITIVE_INFINITY); // Infinity`}
          />
          <TryItButton code={`console.log(Number.POSITIVE_INFINITY);`} />
        </div>
        <OutputBox>Output: Infinity</OutputBox>
      </div>

      {/* EPSILON */}
      <div ref={refs.EPSILON} className="tutorial-section">
        <h3>6️⃣ Number.EPSILON</h3>
        <p>
          The smallest interval between two representable numbers. Useful for
          floating-point comparisons.
        </p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(Number.EPSILON); // 2.220446049250313e-16`}
          />
          <TryItButton code={`console.log(Number.EPSILON);`} />
        </div>
        <OutputBox>Output: 2.220446049250313e-16</OutputBox>
      </div>

      {/* MIN_SAFE_INTEGER */}
      <div ref={refs.MIN_SAFE_INTEGER} className="tutorial-section">
        <h3>7️⃣ Number.MIN_SAFE_INTEGER</h3>
        <p>The minimum safe integer that can be accurately represented.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(Number.MIN_SAFE_INTEGER); // -9007199254740991`}
          />
          <TryItButton code={`console.log(Number.MIN_SAFE_INTEGER);`} />
        </div>
        <OutputBox>Output: -9007199254740991</OutputBox>
      </div>

      {/* MAX_SAFE_INTEGER */}
      <div ref={refs.MAX_SAFE_INTEGER} className="tutorial-section">
        <h3>8️⃣ Number.MAX_SAFE_INTEGER</h3>
        <p>The maximum safe integer that can be accurately represented.</p>
        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`console.log(Number.MAX_SAFE_INTEGER); // 9007199254740991`}
          />
          <TryItButton code={`console.log(Number.MAX_SAFE_INTEGER);`} />
        </div>
        <OutputBox>Output: 9007199254740991</OutputBox>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>Use Number properties to handle numeric limits safely.</li>
          <li>Understand floating-point behavior using Number.EPSILON.</li>
          <li>
            Combine with methods for numeric calculations and conversions.
          </li>
        </ul>
      </div>
    </div>
  );
}
